/**
 * CF-GERMAN-LEGAL-TRANSLATE-V1: German („de“) Terms of Service content.
 *
 * PL #9155: the German (Münzo) build must show its legal pages in German, with
 * ZERO English — a straight translation of the live English Terms
 * (lib/terms-content.ts, v4-2026-06-01), NOT a separate German-legal document.
 * Legal posture is unchanged: the operator is incorporated in Costa Rica and is
 * NOT licensed in / bound by German rules (PL lawyer confirmation), so this is a
 * faithful translation, "as simple as possible, same as the chinese + cricketflip
 * terms".
 *
 * PURE data module — same discipline as lib/terms-content.ts: it imports NOTHING
 * (no app-variant / import.meta), so a ts-jest spec can import it without the
 * import.meta TS1343 break. The render selection (isGerman ? GERMAN_TERMS_TEXT :
 * …) lives in pages/Terms.tsx.
 *
 * Brand-clean by construction: brand is "Münzo", contact is support@muenzo.xyz +
 * the in-app „Feedback senden“ path (the bot is /start-only, so NO Telegram
 * @handle), and there is NO occurrence of "cricketflip" (any case) or any
 * CJK/Chinese-script character. frontend/scripts/variant-check.mts §7 enforces
 * this mechanically.
 *
 * Substance note (flagged to Scorer + PL): the English source lists
 * "…Italy and Germany, among others" as EXCLUDED jurisdictions. A
 * Germany-targeted product cannot tell its own players "we don't accept players
 * from Germany". This translation therefore drops ONLY Germany from the named
 * exclusion list (USA, UK, France, Spain, Netherlands, Italy are kept verbatim) —
 * the minimal, faithful change consistent with PL's Costa-Rica clearance of the
 * German market.
 */
export const GERMAN_TERMS_VERSION = 'de-v1-2026-06-21';

export const GERMAN_TERMS_LAST_UPDATED = '2026-06-21';

export const GERMAN_TERMS_TEXT = `# Münzo — Nutzungsbedingungen

Zuletzt aktualisiert: 21.06.2026

Münzo ist ein nachweisbar faires Münzwurf-Spiel mit TON, gebaut für Telegram. Sage Kopf oder Zahl voraus – gewinnst du, verdoppelt sich dein Einsatz. Jeder Wurf wird signiert und ist überprüfbar. Diese Bedingungen erklären, wie das Spiel funktioniert und welche Regeln gelten.

## Wer spielen darf

Du musst mindestens 18 Jahre alt sein oder das gesetzliche Mindestalter für Online-Spiele an deinem Wohnort erreicht haben – je nachdem, was höher ist. Du bist selbst dafür verantwortlich, dass Spiele um echtes Geld an deinem Wohnort erlaubt sind.

Wir nehmen keine Spieler aus Ländern an, in denen Online-Glücksspiel verboten ist oder eine Lizenz erfordert, die wir nicht besitzen – darunter die USA, Großbritannien, Frankreich, Spanien, die Niederlande und Italien. Wenn es für dich nicht legal ist, spiele bitte nicht.

## So funktioniert es

- Einzahlen – sende TON von deiner Wallet an Münzo. Deine Einzahlung wird deinem Spielguthaben gutgeschrieben, das Münzo hält und in unserem Ledger erfasst – so wie eine Börse dein Guthaben verwahrt. Für deine eigenen Transaktionen fällt die übliche TON-Netzwerkgebühr (Gas) an.
- Spielen – wähle Kopf oder Zahl. Ein Standard-Gewinn zahlt das 2-Fache deines Einsatzes. Boost- und Mega-Würfe zahlen mehr und enthalten eine kleine Gebühr, die dir vor dem Spielen angezeigt wird.
- Prüfen – jedes Wurfergebnis wird von Münzo signiert und erhält eine Wurf-ID, sodass du jedes Ergebnis selbst in der App überprüfen kannst.
- Auszahlen – zahle dein Guthaben jederzeit an deine Wallet aus, in der Regel innerhalb von Sekunden.

## Faires Spiel

Jeder Wurf ist nachweisbar fair: Wir veröffentlichen die Methode, sodass du jedes Ergebnis selbst überprüfen kannst.

Du verpflichtest dich, das Spiel nicht durch Exploits, Automatisierung, Bots oder Absprachen zu manipulieren, den Dienst nicht von einem gesperrten Ort oder für illegale Zwecke zu nutzen und nicht im Namen einer nicht spielberechtigten Person zu spielen. Bei Verstößen gegen diese Regeln können wir Konten sperren oder Einzahlungen erstatten.

## Risiko

Du spielst ein Glücksspiel um echtes Geld. Du kannst deinen Einsatz verlieren – spiele nur mit Geld, dessen Verlust du dir leisten kannst.

Münzo hält dein Guthaben und verarbeitet Einzahlungen, Spiel und Auszahlungen, geschützt durch Ausgabelimits, Überwachung und manuelle Prüfung ungewöhnlicher Auszahlungen. Wir garantieren keinen unterbrechungsfreien Betrieb, keine Verfügbarkeit der Wallet oder des TON-Netzwerks und keinen Schutz vor deinen eigenen Fehlern (falsche Adresse, falscher Betrag, verlorene Wallet-Schlüssel). Soweit gesetzlich zulässig, haftet Münzo nicht für Verluste durch Netzwerkausfälle, Betriebsunterbrechungen, Ereignisse außerhalb unserer Kontrolle oder deine eigenen Entscheidungen. Sollte eine Auszahlung einmal verzögert sein, kontaktiere uns, und wir gleichen sie ab.

## Deine Daten

Wir erheben nur, was wir zum Betrieb des Spiels brauchen:
- deinen Telegram-Benutzernamen und deine Telegram-ID (wenn du die App in Telegram öffnest),
- deine TON-Wallet-Adresse,
- On-Chain-Transaktions-Hashes (auf TON ohnehin öffentlich),
- dein Guthaben, deinen Wurfverlauf und deine Auszahlungsdatensätze,
- anonyme Protokolle zur Fehlersuche und Missbrauchsprävention,
- ein paar Daten im Speicher deines Geräts (Onboarding-Fortschritt und deine Wurf-Belege).

Wir verkaufen deine Daten nicht und geben sie nicht für Marketing weiter. Wir können sie offenlegen, wenn wir gesetzlich dazu verpflichtet sind. Blockchain-Daten sind ihrer Natur nach öffentlich und dauerhaft – wir können sie nicht ändern oder entfernen.

## Verantwortungsvolles Spielen

Glücksspiel sollte Spaß machen und niemals ein Weg sein, um Geld zu verdienen. Wenn es keinen Spaß mehr macht, mach eine Pause. Wenn du glaubst, ein Problem zu haben, hol dir bitte Unterstützung:
- gamblingtherapy.org (weltweit, kostenlos)
- oder eine Beratungsstelle für Glücksspielsucht in deiner Nähe.

## Änderungen

Wir können diese Bedingungen von Zeit zu Zeit aktualisieren. Es gilt immer die aktuelle Fassung, die hier in der App verfügbar ist – schau also ab und zu vorbei.

## Anwendbares Recht & Kontakt

Diese Vereinbarung unterliegt dem Recht von Costa Rica, wo der Betreiber eingetragen ist; Streitigkeiten unterliegen den Gerichten oder der Schiedsgerichtsbarkeit Costa Ricas.

Fragen oder Support: support@muenzo.xyz oder nutze „Feedback senden“ im App-Menü.
`;
